import React, { useState } from 'react';
import {
  View, StyleSheet, Text, ViewStyle,
} from 'react-native';
import {
  VictoryAxis, VictoryChart, VictoryLabel, VictoryVoronoiContainer, VictoryGroup,
} from 'victory';
import Colors from '../../constants/Colors';
import { axisDateLabel, ChartDataPoint, tickValSamples } from '../axisUtils';
import { selectedLineColor } from '../AirQualityGraph';
import { comparisonLineColor } from '../../components/Home/HomeGraphLineSelector';
import XAxis from './components/XAxis';
import Bar from './components/Bar';
import DashedLine from './components/DashedLine';
import Legend from './components/Legend';
import Tooltip from './components/Tooltip';
import { DateRange } from '../../redux/dateRangeSlice';

const chartTheme = {
  axis: {
    stroke: 'none',
    style: {
      stroke: '#696D70',
      tickLabels: { fill: '#696D70' },
      grid: { stroke: 'none' },
    },
  },
};

interface BarGraphProps {
  title?: string,
  alternateTitle?: string,
  chartData: ChartDataPoint[],
  comparisonData?: ChartDataPoint[],
  alternateData?: ChartDataPoint[],
  horizontalTickCount?: number,
  style?: ViewStyle | ViewStyle[],
  showLegend?: boolean,
  chartHeight?: number,
  xAxisTickFormat?,
  unit?: string,
  capacity?: number,
  showLabels?: boolean,
  selectedColor?: string,
  alternateColor?: string,
  stackAlternate?: boolean,
  timeFormat?: boolean,
  axisLabel?: string,
  hideTimeOnTooltip?: boolean,
  range?: DateRange,
  loading?: boolean,
  paddingStartOffset?: number,
}

const BarGraph = ({
  title, alternateTitle, chartData, comparisonData, alternateData, horizontalTickCount = 7, style,
  showLegend, chartHeight = 540, xAxisTickFormat, unit, capacity, showLabels = true,
  alternateColor = Colors.light.hereworksPurple200, stackAlternate = false, timeFormat = false,
  selectedColor = Colors.light.hereworksDarkBlue100, axisLabel, hideTimeOnTooltip, range, loading,
  paddingStartOffset = 0,
}: BarGraphProps) => {
  const paddingTop: number = 20;
  const paddingBottom: number = 50;
  const [chartWidth, setChartWidth] = useState<number>(1000);
  const estimatedBarWidth: number = chartWidth / horizontalTickCount - 45;

  const noData: boolean = !(chartData?.length > 0 || alternateData?.length > 0
    || comparisonData?.length > 0);
  const dataLength: number = Math
    .max(chartData?.length ?? 0, alternateData?.length ?? 0, comparisonData?.length ?? 0);

  const axisTickOffsetX: number = estimatedBarWidth + paddingStartOffset - 26
    + chartWidth * (Math.max(1, 6 - dataLength) * 0.035);
  const paddingStart: number = axisTickOffsetX + 28;

  const domainLimit: number = 1.33 * Math.max(
    ...(chartData ?? []).map((point: ChartDataPoint) => point.value),
    ...(alternateData ?? []).map((point: ChartDataPoint) => point.value),
    ...(comparisonData ?? []).map((point: ChartDataPoint) => point.value),
    1,
  );

  const values: number[] = (chartData ?? []).map((item: { value: number }) => item.value);

  return <View>
    <Text style={styles.yAxisLabel}>
      {axisLabel ?? unit ?? title}
    </Text>
    <View
      onLayout={(e) => setChartWidth(e.nativeEvent.layout.width)}
      {...{ style }}
    >
      <VictoryChart
        width={chartWidth}
        height={chartHeight}
        theme={chartTheme}
        domain={{ y: [0, domainLimit] }}
        containerComponent={<VictoryVoronoiContainer
          labels={({ datum }) => datum}
          responsive={false}
          voronoiDimension='x'
          labelComponent={<Tooltip
            {...{
              chartHeight,
              paddingTop,
              paddingBottom,
              domainLimit,
              title,
              unit,
              alternateTitle,
              alternateData,
              chartData,
              comparisonData,
              timeFormat,
              stackAlternate,
              alternateColor,
              hideTimeOnTooltip,
            }}
            parentWidth={chartWidth}
          />}
        />}
        padding={{
          left: noData ? 40 : paddingStart,
          top: paddingTop,
          bottom: paddingBottom,
          right: chartWidth * 0.03,
        }}
      >
        {XAxis({
          tickValues: tickValSamples(
            [...Array(chartData?.length).keys()],
            Math.min(
              horizontalTickCount,
              Math.floor(
                horizontalTickCount * ((chartData?.length ?? 1) / (comparisonData?.length ?? 1)),
              ),
            ),
          ),
          tickFormat: xAxisTickFormat ?? ((t: number) => ((chartData?.length > 0 && chartData[t])
            ? axisDateLabel(chartData, t, range) : '')),
          fill: Colors.light.hereworksBlack400,
          dy: 20,
          horizontalTickCount,
        })}
        {comparisonData && XAxis({
          tickValues: tickValSamples(
            [...Array(comparisonData?.length).keys()],
            Math.min(
              horizontalTickCount,
              Math.floor(
                horizontalTickCount * ((comparisonData?.length ?? 1) / (chartData?.length ?? 1)),
              ),
            ),
          ),
          tickFormat: (t) => ((comparisonData?.length > 0 && comparisonData[t])
            ? axisDateLabel(comparisonData, t) : ''),
          fill: Colors.light.hereworksYellow600,
          dy: 40,
          horizontalTickCount,
        })}
        <VictoryAxis
          dependentAxis
          domain={{ y: [0, domainLimit ?? 100] }}
          crossAxis={false}
          tickCount={5}
          tickFormat={(value: number) => Math.round((domainLimit > 2 ? 1 : 10) * value)
            / (domainLimit > 2 ? 1 : 10)}
          fixLabelOverlap
          style={{
            axis: { stroke: 'none' },
            tickLabels: { fontSize: 14, lineHeight: 11 },
            grid: { stroke: Colors.light.hereworksWhite500, strokeWidth: 1, pointerEvents: 'none' },
          }}
          containerComponent={<VictoryVoronoiContainer />}
          tickLabelComponent={<VictoryLabel
            style={{
              fill: Colors.light.hereworksBlack400,
              fontSize: 14,
              lineHeight: 11,
              strokeWidth: 0,
              fontFamily: 'Poppins_400Regular',
            }}
            dx={noData ? -20 : -axisTickOffsetX}
            textAnchor='end'
          />}
        />
        {!stackAlternate && chartData?.length > 0 && Bar({
          data: chartData,
          fill: selectedColor,
          activeFill: selectedLineColor,
          cornerRadius: 4,
        })}
        {!stackAlternate && comparisonData?.length > 0 && Bar({
          data: comparisonData,
          fill: Colors.light.hereworksYellow100,
          activeFill: comparisonLineColor,
        })}
        {!stackAlternate && alternateData?.length > 0 && Bar({
          data: alternateData,
          fill: alternateColor,
          activeFill: alternateColor,
        })}
        {stackAlternate && <VictoryGroup
          offset={chartWidth / (dataLength * 4)}
          style={{ data: { width: chartWidth / (dataLength * 4) } }}
        >
          {chartData?.length > 0 && Bar({
            data: chartData,
            fill: selectedColor,
            activeFill: selectedLineColor,
            cornerRadius: 4,
          })}
          {alternateData?.length > 0 && Bar({
            data: alternateData,
            fill: alternateColor,
            activeFill: alternateColor,
          })}
        </VictoryGroup>}
        {showLabels && chartData && DashedLine({
          level: Math.min(...values.filter((value: number) => value > 0)),
          stroke: Colors.light.hereworksGreen300,
          fill: Colors.light.hereworksGreen50,
          title: 'Least',
          unit,
          data: chartData,
          chartHeight,
          timeFormat,
        })}
        {showLabels && chartData && DashedLine({
          level: Math.max(...values),
          stroke: Colors.light.hereworksRed300,
          fill: Colors.light.hereworksRed50,
          title: 'Peak',
          unit,
          data: chartData,
          chartHeight,
          timeFormat,
        })}
        {showLabels && !!capacity && chartData && DashedLine({
          level: capacity,
          stroke: Colors.light.hereworksBlue300,
          fill: Colors.light.hereworksBlue50,
          title: 'Capacity',
          unit,
          data: chartData,
          chartHeight,
          timeFormat,
        })}
      </VictoryChart>
    </View>
    {noData && <View style={styles.descriptionContainer}>
      <Text style={styles.description}>
        {loading ? 'Loading...' : 'No data available'}
      </Text>
    </View>}
    {showLegend && <Legend {...{
      title, alternateTitle, selectedColor, alternateColor,
    }} />}
  </View>;
};

const styles = StyleSheet.create({
  yAxisLabel: {
    fontSize: 14,
    lineHeight: 11,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack200,
    marginBottom: 8,
  },
  descriptionContainer: {
    position: 'absolute',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  description: {
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
});

export default BarGraph;
